<template lang='pug'>
.product-pg(
  :class="[pageLayout, 'product-'+product.type, \
  media.zoom && 'scaled-media', \
  !showTabsSlot && 'no-tabs', \
  !showMainSlot && 'no-main', \
  (product.gallery || []).length < 2 && 'single-img', \
  heroStyle && 'herostyle-'+heroStyle, \
  wallBackground && 'of-img']"
)
  OverflowBackground.grid-media-overflow-bg(
    v-if="wallBackground || pageLayout === 'scalable'"
    @height="media.height = $event"
    :product="product"
  )
  .grid-media-overflow-bg(
    v-else-if="pageLayout !== 'hero'"
  )
  .grid-title(:key="product.productId")
    slot(name="title" :show-h1="true")
  .grid-hero(v-if="pageLayout === 'hero'")
    slot(name="hero")
  .grid-media#pdp-media(:class="media.height !== null && 'loaded'")
    slot(name="media")
      ScalableImage(
        v-if="pageLayout === 'scalable'"
        :source="product.gallery[0].link"
        v-model="media.zoom"
      )
      Media(
        :gallery="product.gallery || []"
        :layout="mediaLayout"
        :product="product"
        :imageFlagMedia="imageFlagMedia"
      )
  StickScroll.grid-rhpa#pdp-rhpa(:style="media.zoom && {'margin-top': media.height +'px'}")
    .rhpa-content
      .title(:key="product.productId")
        slot(name="title")
      .grid-flexible(v-if="product.hasRegistration")
        slot(name="flexible")
      slot(name="rhpa")
      .grid-flexible(v-if="!product.hasRegistration")
        slot(name="flexible")
  .grid-gallery#pdp-gallery(v-if="showGallerySlot")
    slot(name="gallery")
  .grid-tabs#pdp-tabs(v-if="showTabsSlot")
    slot(name="tabs")
  .grid-micro(v-if="showMicropackageSlot")
    slot(name="micropackage")
  .grid-main(v-if="showMainSlot && isContentReady")
    slot(name="main")
  .grid-fill
  .grid-full
    .full-content
      slot(name="full")
    slot(name="bottom")
    slot(name="end")
</template>

<script setup lang="ts">
import { computed, reactive, defineAsyncComponent, type PropType } from 'vue'


const OverflowBackground = defineAsyncComponent(() => import(/* webpackChunkName: "sisu-scalable-pdp" */ './OverflowBackground.vue'))
const ScalableImage = defineAsyncComponent(() => import(/* webpackChunkName: "sisu-scalable-pdp" */ './ScalableImage.vue'))
const StickScroll = defineAsyncComponent(() => import('../core/StickScroll.vue'))

interface ProductGalleryItem { link: string }
interface RogueBuilderProps { layout?: string; background?: string }
interface RogueBuilder { props?: RogueBuilderProps }
interface ProductAttributes { patent_pending?: boolean; is_patented?: boolean }
interface Product {
  productId?: string
  type?: string
  pageLayout?: string
  gallery?: ProductGalleryItem[]
  attributes: ProductAttributes
  roguebuilder?: RogueBuilder
}

const props = defineProps({
  product: {
    type     : Object as PropType<Product>,
    required : true,
  },
  layout: {
    type    : String as PropType<string | null>,
    default : null,
  },
  heroStyle: {
    type    : String as PropType<string | undefined>,
    default : undefined,
  },
  showGallerySlot: {
    type    : Boolean,
    default : false,
  },
  showTabsSlot: {
    type    : Boolean,
    default : false,
  },
  showMainSlot: {
    type    : Boolean,
    default : false,
  },
  showMicropackageSlot: {
    type    : Boolean,
    default : false,
  },
})

const media = reactive<{ zoom: boolean; height: number | null; offsetLeft: number }>({
  zoom       : false,
  height     : null,
  offsetLeft : 0,
})

const pageLayout = computed<string>(() => {
  const rb = props.product.roguebuilder
  const rbProps = (rb || {}).props || {}
  const rbLayout = (rbProps as RogueBuilderProps).layout || (rbProps as RogueBuilderProps).layout
  if (rbLayout) {
    return rbLayout
  }
  const layout = props.product.pageLayout
  if (layout && layout.toLowerCase().indexOf('hero') !== -1) {
    return 'hero'
  }

  return 'standard'
})

const wallBackground = computed<boolean>(() => {
  const rb = props.product.roguebuilder

  return !!(rb && rb.props && rb.props.background && rb.props.background !== 'gray')
})

const mediaLayout = computed<'hero' | 'desktop'>(() => {
  if (pageLayout.value === 'hero' || pageLayout.value === 'scalable') {
    return 'hero'
  }

  return 'desktop'
})

const imageFlagMedia = computed<'patent-pending' | 'patented' | 'none'>(() => {
  if (props.product.attributes.patent_pending) {
    return 'patent-pending'
  }

  if (props.product.attributes.is_patented) {
    return 'patented'
  }

  return 'none'
})

const isContentReady = computed(() => {
  return props.product.productId
})

</script>
<style lang="scss">
.product-pg {
  .grid-tabs {
    img {
      max-width: 100%;
    }
  }

  .grid-tabs:not(:empty) {
    min-height: 350px;
  }
}

// HACKS
.grid-tabs .product-tabs .tab-content > .cms-row.main-container {
  margin: 0 auto
}

</style>
<style lang="scss" scoped>
.product-pg {
  display: grid;
  padding: 0 $space-s;
  max-width: 1920px;
  margin: 0 auto;

  @include not-desktop() {
    grid-template-columns: 1fr;
    grid-template-areas: "title" "hero" "media" "rhpa" "gallery" "tabs" "micro" "main" "full";

    &.no-tabs.no-main {
      grid-template-areas: "title" "hero" "media" "rhpa" "gallery" "micro" "main" "full";
    }

    // Media is full width - bleed it out of the container
    .grid-media {
      margin-left: -$space-s;
      margin-right: -$space-s;
    }
  }

@include all-desktop() {
    grid-template-areas: "hero hero" "media rhpa" "gallery rhpa" "tabs rhpa" "micro rhpa" "main rhpa" "fill rhpa" "full full";
    grid-template-rows: min-content min-content min-content min-content min-content min-content 1fr min-content;

    &.no-tabs.no-main {
      grid-template-areas: "hero hero" "media rhpa" "gallery rhpa" "full full";

      .grid-fill {
        display: none;
      }
    }

    .grid-media, .grid-rhpa {
      margin-top: 54px;
    }

    &.hero {
      // Uncomment to make the media area go on top of the RHPA
      // grid-template-areas: "hero hero" "media media" "tabs rhpa" "main rhpa" "micro rhpa" "fill rhpa" "full full";

      .grid-media, .grid-rhpa {
        margin-top: 0
      }
    }

    &.of-img .grid-media {
      margin-top: 0;
    }
  }

  @include desktop() {
    padding: 0 $space-l 0 $space-xxl; // 16px on the right comes from the RHPA padding
    gap: 0 $space-s;
    grid-template-columns: minmax(300px, 1fr) 407px; //right column is 375px + 16 * 2 padding

    // full width hero
    .grid-hero, .grid-media-overflow-bg {
      margin-left: -$space-xxl;
      margin-right: -$space-l;
    }

    &:not(.hero) .grid-media {
      margin-left: -$space-xxl;
    }

    &.scaled-media {
      .scale-image {
        margin-right: calc(-1 * (16px + 16px + 375px + 16px + 32px));
      }
    }
  }

  @include large-xl() {
    padding: 0 56px 0 $space-xxxxl; //RHPA is 16px: 56px + 16 = 72
    gap: 0 $space-l;
    grid-template-columns: minmax(300px, 1fr) 452px; //right column is 420px + 16px * 2 padding

    // full width hero
    .grid-hero, .grid-media-overflow-bg {
      margin-left: -$space-xxxxl;
      margin-right: -56px;
    }

    &:not(.hero) .grid-media {
      margin-left: -$space-xxxxl;
    }

    &.scaled-media {
      .scale-image {
        margin-right: calc(-1 * (32px + 16px + 420px + 16px + 56px));
      }

      .height-measure {
        width: 100vw;
      }
    }
  }

  @include xl() {
    gap: 0 56px;

    &:not(.hero):not(.scalable) .grid-media {
      margin-left: calc((-1 * (100vw - 1920px + 144px) / 2))
    }

    .grid-media-overflow-bg {
      margin-left: -72px;
      margin-right: -72px;
    }
    .grid-media-overflow-bg {
      margin-right: calc((-1 * (100vw - 1920px + 144px) / 2));
      margin-left: calc((-1 * (100vw - 1920px + 144px) / 2));
    }
  }
}

.grid-main {
  grid-area: main;
  max-width: 100%;
  overflow-x: auto;
}

.height-measure {
  visibility: hidden;
}

.grid-micro {
  margin-top: $space-xl;
  grid-area: micro;
  max-width: 100%;
  min-width: 0;
}

.grid-fill {
  grid-area: fill;
}

.full-content {
  overflow-x: auto;
}

.grid-media-overflow-bg {
  grid-column: 1;
  grid-row: 3;
  margin-left: -$space-s;
  margin-right: -$space-s;
  background-color: $color-gray-x-light;
  z-index: -1;
  margin-bottom: 56px;
}

@include tablet() {
  .grid-media-overflow-bg {
    margin-bottom: 62px;
  }
}

.product-pg.standard, .product-pg.hero{
  #pdp-media {
    @include mobile() {
      aspect-ratio: 16/9;

      &:has(.thumbs), :deep(.gallery-wrap.has-thumbs){
        aspect-ratio: 16/11;
      }
      :deep(.product-media){
        aspect-ratio: 16/9;
      }
    }

    @include tablet() {
      aspect-ratio: 16/9;
      
      :deep(.gallery-wrap.has-thumbs){
        aspect-ratio: 16/10;
      }
    }
  }
}

.custom-bumper{
  .product-pg.standard, .product-pg.hero{
    #pdp-media {
      @include mobile() {
        aspect-ratio: 6/7;
      }

      @include tablet() {
        aspect-ratio: 1/1;
      }
    }
  }
}

.product-pg.standard{
  #pdp-media {
    @include all-desktop() {
      aspect-ratio: 16/7;
    }
  }
}

.of-img {
  #pdp-media {
    opacity: 0;
    transition: opacity 0.3s ease-out;

    &.loaded {
      opacity: 1;
    }
  }

  :deep() {
    .grid-media .i-c {
      background-color: transparent;
    }
  }
}

@include all-desktop() {
  .scalable {
    .grid-rhpa {
      transition: margin-top 0.1s ease-out;
    }
  }

  .product-pg.scalable:not(.single-img) {
    .grid-media-overflow-bg {
      margin-bottom: 245px
    }
  }

  .product-pg.scalable.single-img .grid-media .media.gallery-1 {
    display: none;
  }

  .product-pg.scalable .grid-media :deep() {
    .gallery:not(.pm-zoomed) .media-wrap.image:first-child {
      display: none;
    }
  }

  .scalable.of-img .grid-media-overflow-bg {
    margin-bottom: 40px;
  }

  .scalable:not(.of-img) .grid-rhpa {
    padding-top: 0
  }

  .grid-media-overflow-bg {
    grid-column: 1/-1;
    grid-row: 1/3;
    margin-bottom: 24px;
  }
}

.grid-title {
  display: none;
  grid-area: title;

  @include mobile() {
    display: block;
  }
}

.grid-media {
  grid-area: media;
  min-width: 0;
  margin-bottom: $space-m;

  @include mobile() {
    margin-bottom: $space-m;
  }

  @include tablet() {
    margin-bottom: $space-l;
  }
}

.grid-full {
  margin-top: $space-l;
  min-width: 0;
  grid-area: full;
  height: fit-content;
}

.grid-hero {
  display: none;
  pointer-events: none;

  @include all-desktop() {
    display: block;
    grid-area: hero;
    margin-bottom: $space-s;
  }
}

.grid-rhpa {
  grid-area: rhpa;
  margin-bottom: $space-s;

  .rhpa-content {
    background-color: $color-white;
  }

  @include mobile() {
    .rhpa-content > .title {
      display: none;
    }
  }

  // Sticky RHPA
  @include all-desktop() {
    .rhpa-content {
      padding: $space-s;
    }
  }
}

.grid-gallery {
  grid-area: gallery;
  margin-bottom: $space-s;
  min-width: 0;
  max-width: 100%;
}

.grid-tabs {
  grid-area: tabs;
  margin-bottom: $space-s;
  min-width: 0;

  .product-tabs {
    max-width: 1024px;
    margin: 0 auto;
  }

  // tabs go full width on mobile
  @include mobile() {
    :deep(.product-tabs) {
      margin-left: -$space-s;
      margin-right: -$space-s;
    }
  }
}

</style>
